import { useState } from 'react'
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/contexts/AuthContext'
import { Logo } from '@/components/shared/Logo'
import { adminApi } from '@/api/admin'
import { OnboardingWidget } from '@/components/shared/OnboardingWidget'
import { NotificationBell } from '@/components/shared/NotificationBell'
import { TopBarProvider, TopBarContent } from '@/contexts/TopBarContext'
import { Users, BarChart3, LogOut, Building2, BookOpen, Award, CalendarDays, FileText, ChevronsLeft, ChevronsRight, UserCheck, CalendarRange, Settings, LayoutDashboard, Star, TrendingUp, Globe, Contact, Menu, X, CreditCard, LifeBuoy, GraduationCap, ArrowLeft } from 'lucide-react'

const APP_VERSION = import.meta.env.VITE_APP_VERSION as string | undefined

export default function AdminLayout() {
  return (
    <TopBarProvider>
      <AdminLayoutInner />
    </TopBarProvider>
  )
}

function AdminLayoutInner() {
  const { user, logout, exitImpersonation, impersonatedTenantName } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  // An admin who's also a trainer can switch the whole sidebar into their trimmed trainer
  // view (same /admin/trainer/* routes used standalone) — driven by the URL rather than
  // separate toggle state, so it's deep-linkable and back/forward just works.
  // NOTE: trailing slash matters — '/admin/trainers' (the Trainers management list) also
  // starts with the string '/admin/trainer', so a bare startsWith('/admin/trainer') wrongly
  // matched it too.
  const trainerMode = location.pathname.startsWith('/admin/trainer/')

  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('adminSidebarCollapsed') === 'true'
  )
  const [mobileOpen, setMobileOpen] = useState(false)

  const toggleCollapsed = () => {
    setCollapsed(v => {
      localStorage.setItem('adminSidebarCollapsed', String(!v))
      return !v
    })
  }

  const { data: enquiries = [] } = useQuery({
    queryKey: ['booking-enquiries'],
    queryFn: () => adminApi.getEnquiries().then(r => r.data),
    refetchInterval: 60_000,
  })
  const pendingCount = enquiries.filter(e => e.status === 'Pending').length

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const itemBase = 'flex items-center rounded-lg text-sm font-medium transition-all'
  // Trainer mode gets its own accent (teal instead of the neutral white-overlay admin uses)
  // so it reads unmistakably as a different mode, not just three extra links.
  const itemActive = trainerMode ? 'bg-teal-500/20 text-teal-100' : 'bg-white/15 text-white'
  const itemInactive = trainerMode
    ? 'text-slate-500 hover:bg-teal-500/10 hover:text-teal-200'
    : 'text-slate-500 hover:bg-white/8 hover:text-slate-200'

  // Mobile (< md): always show expanded style regardless of `collapsed`
  // Desktop (md+): respect `collapsed` state
  const navClass = ({ isActive }: { isActive: boolean }) => {
    const active = isActive ? itemActive : itemInactive
    if (collapsed) {
      return `${itemBase} gap-3 px-4 py-2.5 w-full ${active} md:justify-center md:w-10 md:h-10 md:mx-auto md:gap-0 md:px-0 md:py-0`
    }
    return `${itemBase} gap-3 px-4 py-2.5 w-full ${active}`
  }

  // On mobile: always show label. On desktop: spacer when collapsed, label when expanded.
  const SectionLabel = ({ label }: { label: string }) => (
    <>
      <p className="md:hidden text-xs text-slate-500 font-semibold uppercase tracking-wider px-4 pt-4 pb-1">{label}</p>
      {collapsed
        ? <div className="hidden md:block h-3" />
        : <p className="hidden md:block text-xs text-slate-500 font-semibold uppercase tracking-wider px-4 pt-4 pb-1">{label}</p>
      }
    </>
  )

  return (
    <div className="flex h-screen bg-slate-50 print:block">
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar — fixed drawer on mobile, in-flow on desktop */}
      <aside
        className={[
          'fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 flex flex-col shrink-0 print:hidden',
          'transition-all duration-200',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
          'md:relative md:inset-auto md:z-auto md:translate-x-0',
          collapsed ? 'md:w-16' : 'md:w-64',
        ].join(' ')}
      >
        {/* Trainer-mode accent bar — the clearest at-a-glance signal you're in a different mode */}
        <div className={`h-1 shrink-0 transition-colors duration-200 ${trainerMode ? 'bg-teal-500' : 'bg-transparent'}`} />

        {/* Logo / branding */}
        <div className="border-b border-white/8">
          {/* Mobile: full logo + close button */}
          <div className="md:hidden flex items-center justify-between px-6 py-5">
            <Logo size={40} theme="dark" />
            <button
              onClick={() => setMobileOpen(false)}
              className="text-slate-400 hover:text-white p-1 rounded-lg"
              aria-label="Close menu"
            >
              <X size={18} />
            </button>
          </div>
          {/* Desktop: collapsed mark or full logo */}
          <div className={`hidden md:flex ${collapsed ? 'px-3 py-5 justify-center' : 'px-6 py-5'}`}>
            {collapsed ? (
              <Logo size={32} variant="mark" theme="dark" />
            ) : (
              <Logo size={40} theme="dark" />
            )}
          </div>
        </div>

        {/* Nav — onClick closes mobile drawer on navigation */}
        <nav
          className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto scrollbar-none"
          style={{ scrollbarWidth: 'none' }}
          onClick={() => setMobileOpen(false)}
        >
          {trainerMode ? (
            <>
              <NavLink to="/admin/overview" className={navClass} title={collapsed ? 'Back to admin' : undefined}>
                <ArrowLeft size={15} />
                <span className={collapsed ? 'md:hidden' : ''}>Back to admin</span>
              </NavLink>

              <SectionLabel label="My Training" />

              <NavLink to="/admin/trainer/overview" className={navClass} title={collapsed ? 'Overview' : undefined}>
                <LayoutDashboard size={15} />
                <span className={collapsed ? 'md:hidden' : ''}>Overview</span>
              </NavLink>
              <NavLink to="/admin/trainer/schedule" className={navClass} title={collapsed ? 'Schedule' : undefined}>
                <CalendarDays size={15} />
                <span className={collapsed ? 'md:hidden' : ''}>Schedule</span>
              </NavLink>
              <NavLink to="/admin/trainer/calendar" className={navClass} title={collapsed ? 'Calendar' : undefined}>
                <CalendarRange size={15} />
                <span className={collapsed ? 'md:hidden' : ''}>Calendar</span>
              </NavLink>
              <NavLink to="/admin/trainer/courses" className={navClass} title={collapsed ? 'Courses' : undefined}>
                <BookOpen size={15} />
                <span className={collapsed ? 'md:hidden' : ''}>Courses</span>
              </NavLink>
              <NavLink to="/admin/trainer/feedback" className={navClass} title={collapsed ? 'Feedback' : undefined}>
                <Star size={15} />
                <span className={collapsed ? 'md:hidden' : ''}>Feedback</span>
              </NavLink>
            </>
          ) : (
            <>
          <NavLink to="/admin/overview" className={navClass} title={collapsed ? 'Overview' : undefined}>
            <BarChart3 size={15} />
            <span className={collapsed ? 'md:hidden' : ''}>Overview</span>
          </NavLink>

          {!!user?.trainerId && (
            <NavLink
              to="/admin/trainer/overview"
              className={`${itemBase} gap-3 px-4 py-2.5 w-full text-teal-400 hover:bg-teal-500/10 hover:text-teal-300 ${collapsed ? 'md:justify-center md:w-10 md:h-10 md:mx-auto md:gap-0 md:px-0 md:py-0' : ''}`}
              title={collapsed ? 'Trainer view' : undefined}
            >
              <GraduationCap size={15} />
              <span className={collapsed ? 'md:hidden' : ''}>Trainer view</span>
            </NavLink>
          )}

          <SectionLabel label="People" />

          <NavLink to="/admin/clients" className={navClass} title={collapsed ? 'Clients' : undefined}>
            <Users size={15} />
            <span className={collapsed ? 'md:hidden' : ''}>Clients</span>
          </NavLink>

          <NavLink to="/admin/trainers" className={navClass} title={collapsed ? 'Trainers' : undefined}>
            <UserCheck size={15} />
            <span className={collapsed ? 'md:hidden' : ''}>Trainers</span>
          </NavLink>

          <NavLink to="/admin/attendees" className={navClass} title={collapsed ? 'Attendees' : undefined}>
            <Contact size={15} />
            <span className={collapsed ? 'md:hidden' : ''}>Attendees</span>
          </NavLink>

          <SectionLabel label="Training" />

          <NavLink to="/admin/courses" className={navClass} title={collapsed ? 'Courses' : undefined}>
            <BookOpen size={15} />
            <span className={collapsed ? 'md:hidden' : ''}>Courses</span>
          </NavLink>

          <NavLink to="/admin/certificate-templates" className={navClass} title={collapsed ? 'Certificates' : undefined}>
            <Award size={15} />
            <span className={collapsed ? 'md:hidden' : ''}>Certificates</span>
          </NavLink>

          <SectionLabel label="Schedule" />

          <NavLink to="/admin/calendar" className={navClass} title={collapsed ? 'Calendar' : undefined}>
            <CalendarRange size={15} />
            <span className={collapsed ? 'md:hidden' : ''}>Calendar</span>
          </NavLink>

          <NavLink to="/admin/sessions" className={navClass} title={collapsed ? 'Public Sessions' : undefined}>
            <Globe size={15} />
            <span className={collapsed ? 'md:hidden' : ''}>Public Sessions</span>
          </NavLink>

          {/* Bookings — pending badge */}
          <NavLink to="/admin/bookings" className={navClass} title={collapsed ? 'Client Bookings' : undefined}>
            {({ isActive }) => (
              <>
                <div className="relative">
                  <CalendarDays size={15} />
                  {/* Dot badge: desktop collapsed only */}
                  {pendingCount > 0 && collapsed && (
                    <span className="hidden md:block absolute -top-1.5 -right-1.5 w-2.5 h-2.5 rounded-full bg-amber-500 border-2 border-slate-900" />
                  )}
                </div>
                <span className={collapsed ? 'md:hidden' : ''}>Client Bookings</span>
                {/* Number badge: always on mobile, only when expanded on desktop */}
                {pendingCount > 0 && (
                  <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold ${
                    isActive ? 'bg-white text-slate-900' : 'bg-amber-500 text-white'
                  } ${collapsed ? 'md:hidden' : ''}`}>
                    {pendingCount}
                  </span>
                )}
              </>
            )}
          </NavLink>

          <SectionLabel label="Business" />

          <NavLink to="/admin/invoices" className={navClass} title={collapsed ? 'Invoices' : undefined}>
            <FileText size={15} />
            <span className={collapsed ? 'md:hidden' : ''}>Invoices</span>
          </NavLink>

          <NavLink to="/admin/payments" className={navClass} title={collapsed ? 'Payments' : undefined}>
            <CreditCard size={15} />
            <span className={collapsed ? 'md:hidden' : ''}>Payments</span>
          </NavLink>

          <NavLink to="/admin/feedback" className={navClass} title={collapsed ? 'Feedback' : undefined}>
            <Star size={15} />
            <span className={collapsed ? 'md:hidden' : ''}>Feedback</span>
          </NavLink>

          <NavLink to="/admin/analytics" className={navClass} title={collapsed ? 'Analytics' : undefined}>
            <TrendingUp size={15} />
            <span className={collapsed ? 'md:hidden' : ''}>Analytics</span>
          </NavLink>

          <SectionLabel label="Settings" />

          <NavLink to="/admin/company" className={navClass} title={collapsed ? 'My Company' : undefined}>
            <Building2 size={15} />
            <span className={collapsed ? 'md:hidden' : ''}>My Company</span>
          </NavLink>

          <NavLink to="/admin/support" className={navClass} title={collapsed ? 'Help & support' : undefined}>
            <LifeBuoy size={15} />
            <span className={collapsed ? 'md:hidden' : ''}>Help &amp; support</span>
          </NavLink>
            </>
          )}
        </nav>

        {/* Bottom: user pill + sign out + collapse toggle */}
        <div className="px-3 py-2 border-t border-white/8 space-y-0.5">
          {/* User pill */}
          <NavLink
            to="/admin/account"
            onClick={() => setMobileOpen(false)}
            title={collapsed ? 'Account settings' : undefined}
            className={({ isActive }) =>
              collapsed
                ? `${itemBase} gap-2.5 px-3 py-1.5 w-full ${isActive ? itemActive : itemInactive} md:justify-center md:w-10 md:h-8 md:mx-auto md:gap-0 md:px-0`
                : `${itemBase} gap-2.5 px-3 py-1.5 w-full ${isActive ? itemActive : itemInactive}`
            }
          >
            <span className="shrink-0 w-6 h-6 rounded-full bg-indigo-500 flex items-center justify-center text-white text-[10px] font-semibold uppercase">
              {user?.email?.[0] ?? '?'}
            </span>
            <span className={`flex-1 min-w-0 ${collapsed ? 'md:hidden' : ''}`}>
              <span className="block text-xs font-medium text-slate-200 truncate">{user?.email}</span>
            </span>
            <Settings size={13} className={`shrink-0 text-slate-500 ${collapsed ? 'md:hidden' : ''}`} />
          </NavLink>

          {/* Mobile: sign out only (no collapse toggle) */}
          <button
            onClick={handleLogout}
            className={`md:hidden ${itemBase} cursor-pointer ${itemInactive} gap-2 px-3 py-1.5 w-full`}
          >
            <LogOut size={14} />
            <span className="text-xs">Sign out</span>
          </button>

          {/* Desktop: sign out + collapse toggle */}
          {collapsed ? (
            <>
              <button
                onClick={handleLogout}
                title="Sign out"
                className={`hidden md:flex ${itemBase} cursor-pointer ${itemInactive} justify-center w-10 h-8 mx-auto`}
              >
                <LogOut size={15} />
              </button>
              <button
                onClick={toggleCollapsed}
                title="Expand sidebar"
                className={`hidden md:flex ${itemBase} cursor-pointer text-slate-600 hover:bg-white/8 hover:text-slate-400 justify-center w-10 h-8 mx-auto`}
              >
                <ChevronsRight size={14} />
              </button>
            </>
          ) : (
            <div className="hidden md:flex items-center gap-1">
              <button
                onClick={handleLogout}
                className={`${itemBase} cursor-pointer ${itemInactive} gap-2 px-3 py-1.5 flex-1`}
              >
                <LogOut size={14} />
                <span className="text-xs">Sign out</span>
              </button>
              <button
                onClick={toggleCollapsed}
                title="Collapse sidebar"
                className={`${itemBase} cursor-pointer text-slate-600 hover:bg-white/8 hover:text-slate-400 px-2 py-1.5 rounded-lg`}
              >
                <ChevronsLeft size={14} />
              </button>
            </div>
          )}
          {!collapsed && APP_VERSION && (
            <p className="hidden md:block text-center text-[10px] text-slate-600 pt-1">v{APP_VERSION}</p>
          )}
        </div>
      </aside>

      <main className="flex-1 flex flex-col overflow-hidden print:block">
        {impersonatedTenantName && (
          <div className="bg-amber-500 text-white text-sm font-medium px-6 py-2 flex items-center justify-between shrink-0 print:hidden">
            <span>Impersonating <strong>{impersonatedTenantName}</strong></span>
            <button
              onClick={() => { exitImpersonation(); navigate('/superadmin/tenants') }}
              className="text-xs underline hover:no-underline cursor-pointer"
            >
              Exit impersonation
            </button>
          </div>
        )}
        <header className="h-12 bg-white border-b border-slate-200 flex items-center justify-between px-4 md:px-6 shrink-0 print:hidden">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => setMobileOpen(true)}
              className="md:hidden p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              aria-label="Open menu"
            >
              <Menu size={20} />
            </button>
            <TopBarContent />
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <OnboardingWidget />
            <NotificationBell
              api={adminApi}
              isAdmin
              buttonClass="text-slate-500 hover:bg-slate-100 hover:text-slate-700 rounded-lg"
            />
          </div>
        </header>
        <div className="flex-1 overflow-auto print:overflow-visible">
          <div className="max-w-6xl mx-auto px-4 py-6 sm:px-6 sm:py-8 md:px-10 md:py-10 print:px-0 print:py-0 print:max-w-none">
            <Outlet />
          </div>
        </div>
      </main>

    </div>
  )
}
